import _ from 'lodash'
import moment from 'moment'

import i18n from '@/i18n/i18n'
import ApplicationService from '@/services/admin/ApplicationService.js'
import { build_query, build_query_with_prefix, errorMessage } from '@/utilities/helpers.js'
import money from '@/utilities/money'

import { APPLICATION_STATUSES, APPLICATION_DECISIONS } from '@/constants/constants'
import { APPLICATION_ERROR_MESSAGES } from '@/constants/errorMessages'
import { SALES_CHANNEL_TYPE } from '@/constants/salesChannels'

const { t } = i18n.global

export const namespaced = true

export const allowedFilters = [
  'farmer_id',
  'retailer_id',
  'company_id',
  'sales_channel_id',
  'created_between',
  'payment_date_between',
  'status',
  'decision',
  'applicant_type',
  'applicant_id',
  'by_region',
  'v4',
  'by_original_platforms'
]

const allowedIncludes =
  'farmer,' +
  'company,' +
  'retailer,' +
  'retailer.address.city.region,' +
  'applicant,' +
  'retailerUser,' +
  'order,' +
  'order.items,' +
  'order.items.product,' +
  'order.items.unit,' +
  'order.items.productUnit,' +
  'order.items.brand,' +
  'saleschannel,' +
  'score,' +
  'decisions,' +
  'decisionsOnlyTrashed,' +
  'decisionsOnlyTrashed.activities,' +
  'decisionsOnlyTrashed.activities.activist,' +
  'crops,' +
  'assignee,' +
  'guarantors,' +
  'anomalyScore,' +
  'dsPotentialFraud'

export const allowedSorts = ['isWaitingApprove', '-created_at']

export const allowedAppends = [
  'outstanding_balance',
  'last_decision',
  'retailer_anomaly',
  'last_decision_owner',
  'is_assignable',
  'is_document_required',
  'required_documents',
  'farmer_application_location'
]

export const state = {
  applications: null,
  statistics: {},
  activities: [],
  previousApplications: null,
  decisionReasons: null,
  application: {},
  guarantor: null,
  meta: null,
  survey: null,
  creditReports: [],
  farmerDocuments: [],
  apiaProduction: null
}

export const mutations = {
  SET_APPLICATIONS(state, applications) {
    if (applications?.data) {
      state.applications = applications.data.map(item => ({
        ...item,
        sales_channel: item.saleschannel,
        deleted_at: item.order?.deleted_at,
        saleschannel: undefined
      }))
    } else {
      state.applications = []
    }

    state.meta = applications?.meta || null
  },
  SET_APPLICATION(state, application) {
    state.application = application
  },
  SET_STATISTICS(state, statistics) {
    state.statistics = statistics
  },
  CHANGE_APPLICATION_STATUS(state, status) {
    state.application.status = status
  },
  CHANGE_APPLICATION_DECISION(state, decision) {
    state.application.decision = decision
  },
  SET_APPLICATION_ASSIGNEE(state, assignee) {
    state.application.assignee = assignee
    state.application.assignee_id = assignee && assignee.id
  },
  SET_ANSWERS(state, answers) {
    state.survey = answers
  },
  SET_FARMER_DOCUMENTS(state, farmerDocuments) {
    state.farmerDocuments = farmerDocuments
  },
  SET_ACTIVITIES(state, activities) {
    state.activities = activities
  },
  SET_PREVIOUS_APPLICATIONS(state, applications) {
    state.previousApplications = applications
  },
  SET_DECISION_REASONS(state, reasons) {
    state.decisionReasons = reasons
  },
  SET_CREDIT_REPORTS(state, creditReports) {
    state.creditReports = creditReports
  },
  CHANGE_RETAILER_ANOMALY(state, anomaly) {
    state.application.decisions.map(decision => {
      if (decision.type === 'RetailerAnomalySpecification') {
        decision.data.retailer_anomaly_status = 0
      }
      return decision
    })
    state.application.retailer_anomaly = anomaly
  },
  DELETE_DECISION(state) {
    state.application.last_decision.deleted_at = moment()
    _.last(state.application.decisions).deleted_at = moment()
  },
  CHANGE_APPLICATION_LAST_DECISION(state, decision) {
    state.application.last_decision = decision
    state.application.decisions.push(decision)
  },
  UPDATE_BUSINESS_NUMBER(state, application) {
    state.application.business_number = application.business_number
  },
  UPDATE_REQUIRED_DOCUMENTS(state, documents) {
    state.application.required_documents = documents
  },
  SET_GUARANTOR(state, guarantor) {
    state.guarantor = guarantor
  },
  SET_APIA_PRODUCTION(state, data) {
    state.apiaProduction = data
  }
}

const getApplicationDecisionReason = application => {
  let decision = null
  if (application.decisions.length) {
    decision = _.last(application.decisions).label
  }
  if (application.last_decision) {
    switch (application.last_decision.type) {
      case 'HighAmountRequestedSpecification':
        decision =
          decision +
          t('store.admin.application.offered_limit_amount_for_allotment') +
          money.ofMinor(application.last_decision.data.limit).getFormattedAmount()
        break
      case 'ScoreRejectSpecificationWithLimit':
        decision =
          decision +
          t('store.admin.application.ml_offered_limit_amount') +
          money.ofMinor(application.last_decision.data.limit).getFormattedAmount()
        break
      case 'ScoreApproveSpecification':
        if (application.score && application.score.data.risk_pool_type) {
          decision = 'R' + application.score.data.risk_pool_type + t('store.admin.application.approve')
        }
      case 'CreditLimitApproveSpecification':
        decision =
          t('store.admin.application.credit_limit_auto_approve') +
          money.ofMinor(application.last_decision.data.credit_limit_amount).getFormattedAmount()
        break
    }
  }
  return decision
}

const getApplicationRetailerUser = application => {
  let applicant = application.applicant ? application.applicant : application.retailer_user

  return applicant.firstname + ' ' + applicant.lastname
}

export const getters = {
  applications: state => state.applications,
  applicationExists: state => id => {
    if (state.applications) {
      const application = state.applications.find(application => application.id === id)
      return application
    }
    return false
  },
  getApplication: state => state.application,
  getApplicationDecisionReason: state => applicationId => {
    let application = state.applications.find(item => item.id === applicationId)
    return getApplicationDecisionReason(application)
  },
  getPreviousApplicationDecisionReason: state => application => {
    return getApplicationDecisionReason(application)
  },
  getApplicationRetailerUser: state => applicationId => {
    let application = state.applications.find(item => item.id === applicationId)
    return getApplicationRetailerUser(application)
  },
  getCreditReports: state => state.creditReports
}

export const actions = {
  setApplicationAssignee({ commit }, assignee) {
    commit('SET_APPLICATION_ASSIGNEE', assignee)
  },
  getApplications({ commit, dispatch }, query) {
    let newQuery = build_query(query, allowedFilters, allowedIncludes, allowedSorts, allowedAppends)

    return new Promise((resolve, reject) => {
      ApplicationService.getApplications(newQuery)
        .then(response => {
          commit('SET_APPLICATIONS', response)

          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: APPLICATION_ERROR_MESSAGES.getApplicationsError + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getStatistics({ commit, dispatch }, query) {
    return new Promise((resolve, reject) => {
      let newQuery = build_query_with_prefix(query, allowedFilters, 'application')
      ApplicationService.getStatistics(newQuery)
        .then(response => {
          commit('SET_STATISTICS', response)

          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: APPLICATION_ERROR_MESSAGES.statisticsError + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getPreviousApplications({ commit, dispatch }) {
    let newQuery = build_query(
      {
        'filter[farmer_id]': state.application.farmer.id,
        'filter[beforeCreated]': moment(state.application.created_at).format('YYYY-MM-DD HH:mm'),
        'filter[status]': '1,2,3,4,5,6',
        per_page: 50
      },
      ['farmer'],
      'order,order.payments,order.payments.collections,decisions,score',
      allowedSorts,
      ['last_decision']
    )

    return new Promise((resolve, reject) => {
      ApplicationService.getApplications(newQuery)
        .then(response => {
          commit('SET_PREVIOUS_APPLICATIONS', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: APPLICATION_ERROR_MESSAGES.getApplicationsError + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getApplication({ commit, dispatch, getters }, id) {
    const application = getters.applicationExists(id)
    if (application) {
      commit('SET_APPLICATION', application)
      commit('SET_GUARANTOR', _.last(application.guarantors))

      return application
    }
    return new Promise((resolve, reject) => {
      ApplicationService.getApplication(id)
        .then(response => {
          commit('SET_APPLICATION', response.data)
          commit('SET_GUARANTOR', _.last(response.data.guarantors))

          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: APPLICATION_ERROR_MESSAGES.getApplicationError + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  refreshApplication({ commit, dispatch }, id) {
    return new Promise((resolve, reject) => {
      ApplicationService.getApplication(id)
        .then(response => {
          commit('SET_APPLICATION', response.data)

          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: APPLICATION_ERROR_MESSAGES.getApplicationError + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  assign({ dispatch, commit, state }, params) {
    return new Promise((resolve, reject) => {
      ApplicationService.assign(state.application.id, params)
        .then(response => {
          commit('SET_APPLICATION_ASSIGNEE', response.data.assignee)

          const notification = {
            type: 'success',
            message:
              params && params.remove
                ? t('store.shared.removed', {
                  element: t('store.admin.application.application_assignment')
                })
                : t('store.admin.application.application_assigned')
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.admin.application.an_error_occurred_while_assignment') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  fraudDecision({ dispatch, commit, state }, decision) {
    return new Promise((resolve, reject) => {
      ApplicationService.fraudDecision(state.application.id, decision)
        .then(response => {
          commit('CHANGE_APPLICATION_STATUS', response.data.status)
          const notification = {
            type: 'success',
            message: t('store.admin.application.fraud_decision_saved')
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  farmerVerificationDocumentApprove({ dispatch, commit, state }) {
    return new Promise((resolve, reject) => {
      ApplicationService.transition(state.application.id, APPLICATION_STATUSES.SCORE_WAITING, {
        decision: APPLICATION_DECISIONS.DRAFT
      })
        .then(response => {
          commit('CHANGE_APPLICATION_STATUS', response.data.status)
          const notification = {
            type: 'success',
            message: t('store.admin.application.farmer_document_approved')
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  confirm({ dispatch, commit, state }, params) {
    return new Promise((resolve, reject) => {
      if (
        [
          SALES_CHANNEL_TYPE.DIRECT_FORWARD,
          SALES_CHANNEL_TYPE.FORWARD_SALE,
          SALES_CHANNEL_TYPE.FORWARD_SALE_WITH_RETAILER_DELAYED,
          SALES_CHANNEL_TYPE.ROMANIA_FORWARD_SALE,
          SALES_CHANNEL_TYPE.ROMANIA_FORWARD_SALE_WITH_RETAILER_DELAYED,
          SALES_CHANNEL_TYPE.ROMANIA_PRIVATE_INDIVIDUAL
        ].includes(state.application.sales_channel_id)
      ) {
        ApplicationService.transition(state.application.id, APPLICATION_STATUSES.APPROVED, {
          decision: APPLICATION_DECISIONS.APPROVED,
          ...params
        })
          .then(response => {
            commit('CHANGE_APPLICATION_STATUS', response.data.status)
            commit('CHANGE_APPLICATION_DECISION', response.data.decision)

            const notification = {
              type: 'success',
              message: t('store.shared.approved', {
                element: t('store.admin.application.application')
              })
            }
            dispatch('notification/add', notification, {
              root: true
            })
            resolve(response.data)
          })
          .catch(err => {
            const notification = {
              type: 'danger',
              message: APPLICATION_ERROR_MESSAGES.confirmError + errorMessage(err)
            }
            dispatch('notification/add', notification, {
              root: true
            })
            reject(err)
          })
      }
    })
  },
  conditionalConfirm({ commit, dispatch, state }) {
    return new Promise((resolve, reject) => {
      if (
        [
          SALES_CHANNEL_TYPE.DIRECT_FORWARD,
          SALES_CHANNEL_TYPE.FORWARD_SALE,
          SALES_CHANNEL_TYPE.FORWARD_SALE_WITH_RETAILER_DELAYED
        ].includes(state.application.sales_channel_id)
      ) {
        ApplicationService.transition(state.application.id, APPLICATION_STATUSES.APPROVED, {
          decision: APPLICATION_DECISIONS.CONDITIONAL_APPROVED
        })
          .then(response => {
            commit('CHANGE_APPLICATION_STATUS', response.data.status)
            commit('CHANGE_APPLICATION_DECISION', response.data.decision)

            const notification = {
              type: 'success',
              message: t('store.admin.application.application_approved_by_guarantor')
            }
            dispatch('notification/add', notification, {
              root: true
            })
            resolve(response.data)
          })
          .catch(err => {
            const notification = {
              type: 'danger',
              message: APPLICATION_ERROR_MESSAGES.confirmError + errorMessage(err)
            }
            dispatch('notification/add', notification, {
              root: true
            })
            reject(err)
          })
      }
    })
  },
  confirmRejectedApplication({ commit, dispatch, state }) {
    return new Promise((resolve, reject) => {
      if (
        [
          SALES_CHANNEL_TYPE.DIRECT_FORWARD,
          SALES_CHANNEL_TYPE.FORWARD_SALE,
          SALES_CHANNEL_TYPE.FORWARD_SALE_WITH_RETAILER_DELAYED
        ].includes(state.application.sales_channel_id)
      ) {
        ApplicationService.transition(state.application.id, APPLICATION_STATUSES.APPROVED, {
          decision: APPLICATION_DECISIONS.APPROVED
        })
          .then(response => {
            commit('CHANGE_APPLICATION_STATUS', response.data.status)
            commit('CHANGE_APPLICATION_DECISION', response.data.decision)

            const notification = {
              type: 'success',
              message: t('store.admin.application.rejected_application_approved')
            }
            dispatch('notification/add', notification, {
              root: true
            })
            resolve(response.data)
          })
          .catch(err => {
            const notification = {
              type: 'danger',
              message: `${APPLICATION_ERROR_MESSAGES.confirmError} ${errorMessage(err)}`
            }
            dispatch('notification/add', notification, {
              root: true
            })
            reject(err)
          })
      }
    })
  },
  conditionalConfirmRejectedApplication({ commit, dispatch, state }) {
    return new Promise((resolve, reject) => {
      if (
        [
          SALES_CHANNEL_TYPE.DIRECT_FORWARD,
          SALES_CHANNEL_TYPE.FORWARD_SALE,
          SALES_CHANNEL_TYPE.FORWARD_SALE_WITH_RETAILER_DELAYED
        ].includes(state.application.sales_channel_id)
      ) {
        ApplicationService.transition(state.application.id, APPLICATION_STATUSES.APPROVED, {
          decision: APPLICATION_DECISIONS.CONDITIONAL_APPROVED
        })
          .then(response => {
            commit('CHANGE_APPLICATION_STATUS', response.data.status)
            commit('CHANGE_APPLICATION_DECISION', response.data.decision)

            const notification = {
              type: 'success',
              message: t('store.admin.application.rejected_application_approved_by_guarantor')
            }
            dispatch('notification/add', notification, {
              root: true
            })
            resolve(response.data)
          })
          .catch(err => {
            const notification = {
              type: 'danger',
              message: APPLICATION_ERROR_MESSAGES.confirmError + errorMessage(err)
            }
            dispatch('notification/add', notification, {
              root: true
            })
            reject(err)
          })
      }
    })
  },
  saveMuLimit({ commit, dispatch, state }, amount) {
    return new Promise((resolve, reject) => {
      ApplicationService.saveMuLimit(state.application.id, amount)
        .then(response => {
          commit('CHANGE_APPLICATION_LAST_DECISION', response.data.last_decision)
          const notification = {
            type: 'warning',
            message: t('store.admin.application.ml_defined_an_allocation_limit_for_rejected_application')
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.admin.application.an_error_occurred_while_ml_defining_allocation_limit') + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  reject({ commit, dispatch, state }, reason) {
    return new Promise((resolve, reject) => {
      if (
        [
          SALES_CHANNEL_TYPE.DIRECT_FORWARD,
          SALES_CHANNEL_TYPE.FORWARD_SALE,
          SALES_CHANNEL_TYPE.FORWARD_SALE_WITH_RETAILER_DELAYED,
          SALES_CHANNEL_TYPE.ROMANIA_FORWARD_SALE,
          SALES_CHANNEL_TYPE.ROMANIA_FORWARD_SALE_WITH_RETAILER_DELAYED,
          SALES_CHANNEL_TYPE.ROMANIA_PRIVATE_INDIVIDUAL
        ].includes(state.application.sales_channel_id)
      ) {
        ApplicationService.transition(state.application.id, APPLICATION_STATUSES.REJECTED, {
          ...reason,
          decision: APPLICATION_DECISIONS.REJECTED
        })
          .then(response => {
            commit('CHANGE_APPLICATION_STATUS', response.data.status)
            commit('CHANGE_APPLICATION_DECISION', response.data.decision)

            const notification = {
              type: 'success',
              message: t('store.shared.rejected', {
                element: t('store.admin.application.application')
              })
            }
            dispatch('notification/add', notification, {
              root: true
            })
            resolve(response.data)
          })
          .catch(err => {
            const notification = {
              type: 'danger',
              message: APPLICATION_ERROR_MESSAGES.confirmError + errorMessage(err)
            }
            dispatch('notification/add', notification, {
              root: true
            })
            reject(err)
          })
      }
    })
  },
  documentWaiting({ commit, dispatch, state }) {
    return new Promise((resolve, reject) => {
      ApplicationService.documentWaiting(state.application.id, APPLICATION_STATUSES.DOCUMENT_WAITING, {})
        .then(response => {
          commit('CHANGE_APPLICATION_STATUS', response.data.status)

          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getAnswers({ commit, dispatch }, applicationId) {
    return new Promise((resolve, reject) => {
      ApplicationService.getAnswers(applicationId)
        .then(response => {
          commit('SET_ANSWERS', response.data)

          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.fetching_problem', {
                element: t('store.admin.application.survey_answers')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getActivities({ commit, dispatch }, applicationId) {
    return new Promise((resolve, reject) => {
      ApplicationService.getActivities(applicationId)
        .then(response => {
          commit('SET_ACTIVITIES', response.data)

          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.fetching_problem', {
                element: t('store.admin.application.application_activities')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getDecisionReasons({ commit, dispatch }, applicationId) {
    return new Promise((resolve, reject) => {
      ApplicationService.getDecisionReasons()
        .then(response => {
          commit('SET_DECISION_REASONS', response.data)

          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.fetching_problem', {
                element: t('store.admin.application.application_assignment')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getCreditReports({ commit, dispatch }, applicationId) {
    return new Promise((resolve, reject) => {
      ApplicationService.getCreditReports(applicationId)
        .then(response => {
          commit('SET_CREDIT_REPORTS', response.data)

          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.fetching_problem', {
                element: t('store.admin.application.application_assignment')
              }) +
              ' ' +
              errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  deleteDecision({ commit, dispatch, state }, params) {
    return new Promise((resolve, reject) => {
      ApplicationService.deleteDecision(params.applicationId, params.decisionId)
        .then(response => {
          commit('DELETE_DECISION')
          const notification = {
            type: 'success',
            message: t('store.shared.deleted', {
              element: t('store.admin.application.decision')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.delete_error', {
                element: t('store.admin.application.decision')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  exportKit({ dispatch }, data) {
    return new Promise(reject => {
      ApplicationService.exportKit(data.query, allowedFilters, data.parameter)
        .then(() => {
          const notification = {
            type: 'info',
            message: t('store.shared.exporting', {
              element: t('store.admin.application.applications')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.exporting_error', {
                element: t('store.admin.application.applications')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  updateBusinessNumber({ commit, dispatch }, data) {
    return new Promise((resolve, reject) => {
      ApplicationService.updateBusinessNumber(data.applicationId, data.businessNumber)
        .then(response => {
          commit('UPDATE_BUSINESS_NUMBER', response.data)
          const notification = {
            type: 'success',
            message: t('store.shared.updated', {
              element: t('store.admin.application.business_number')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.update_error', {
                element: t('store.admin.application.business_number')
              }) + errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  documentRequired({ dispatch, commit, state }, params) {
    return new Promise((resolve, reject) => {
      if (
        [
          SALES_CHANNEL_TYPE.FORWARD_SALE,
          SALES_CHANNEL_TYPE.FORWARD_SALE_WITH_RETAILER_DELAYED,
          SALES_CHANNEL_TYPE.DIRECT_FORWARD
        ].includes(state.application.sales_channel_id)
      ) {
        ApplicationService.saveRequiredDocuments(state.application.id, params)
          .then(response => {
            commit('CHANGE_APPLICATION_STATUS', response.data.status)
            commit('UPDATE_REQUIRED_DOCUMENTS', response.data.required_documents)

            const notification = {
              type: 'success',
              message: t('store.admin.application.application_moved_document_required_status')
            }
            dispatch('notification/add', notification, {
              root: true
            })
            resolve(response.data)
          })
          .catch(err => {
            const notification = {
              type: 'danger',
              message: errorMessage(err)
            }
            dispatch('notification/add', notification, {
              root: true
            })
            reject(err)
          })
      }
    })
  },
  uploadRequiredDocuments({ dispatch, commit, state }, files) {
    return new Promise((resolve, reject) => {
      ApplicationService.uploadRequiredDocuments(state.application.id, files)
        .then(response => {
          commit('UPDATE_REQUIRED_DOCUMENTS', response.data.required_documents)
          const notification = {
            type: 'success',
            message: t('store.admin.application.selected_documents_uploaded')
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  viewRequiredDocument({ dispatch, state }, document) {
    return new Promise((resolve, reject) => {
      ApplicationService.viewRequiredDocument(state.application.id, document)
        .then(response => {
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  downloadRequiredDocument({ dispatch, state }, document) {
    return new Promise((resolve, reject) => {
      ApplicationService.downloadRequiredDocument(state.application.id, document)
        .then(response => {
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  updateStatusAsAdWaiting({ dispatch, commit, state }) {
    return new Promise((resolve, reject) => {
      ApplicationService.transition(state.application.id, APPLICATION_STATUSES.AD_WAITING, {
        decision: APPLICATION_DECISIONS.DRAFT
      })
        .then(response => {
          commit('CHANGE_APPLICATION_STATUS', response.data.status)
          const notification = {
            type: 'success',
            message: t('store.admin.application.application_sent_allocation_unit')
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  deleteRequiredDocument({ dispatch, state }, documentId) {
    return new Promise((resolve, reject) => {
      ApplicationService.deleteRequiredDocument(documentId)
        .then(response => {
          const notification = {
            type: 'success',
            message: t('store.admin.application.document_successfully_deleted')
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getFarmerDocuments({ commit, dispatch }, applicationId) {
    return new Promise((resolve, reject) => {
      ApplicationService.getFarmerDocuments(applicationId)
        .then(response => {
          commit('SET_FARMER_DOCUMENTS', response.data)

          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getApiaProduction({ commit, dispatch }, applicationId) {
    return new Promise((resolve, reject) => {
      ApplicationService.getApiaProduction(applicationId)
        .then(response => {
          if (!Array.isArray(response.data) && Object.keys(response.data).length > 0) {
            commit('SET_APIA_PRODUCTION', response.data)
          } else {
            commit('SET_APIA_PRODUCTION', null)
          }

          resolve(response.data)
        })
        .catch(err => {
          commit('SET_APIA_PRODUCTION', null)

          const notification = {
            type: 'danger',
            message: errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  }
}
